import { useQuery } from "@tanstack/react-query";
import React from "react";

import { fetchRowHistory } from "../lib/audit-api";
import { AUDIT_SOURCE_NOTE } from "../lib/audit-history";
import { useAuth } from "../lib/useAuth";
import { AuditTimeline } from "./AuditTimeline";

type RowHistoryProps = {
  datasourceId: string;
  collection: string;
  rowKey: string;
};

/**
 * The History tab of the row drawer (MOSA-368 slice 1).
 *
 * The API decides what this reader may see: a member reaches the history of a
 * row only through a tab they can open. A row with no events is the normal
 * first state, so it reads as a fact rather than as an error.
 */
export const RowHistory: React.FC<RowHistoryProps> = ({
  datasourceId,
  collection,
  rowKey,
}) => {
  const { token } = useAuth();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["rowHistory", datasourceId, collection, rowKey],
    queryFn: () => fetchRowHistory(datasourceId, collection, rowKey, { token }),
  });

  if (isLoading) {
    return <p className="m-0 text-sm text-od-muted">Loading the history…</p>;
  }

  if (isError) {
    return (
      <p className="m-0 text-sm text-od-warning">
        Could not load the history of this row. Close the record and open it
        again.
      </p>
    );
  }

  const events = data?.items ?? [];

  return (
    <div className="space-y-2">
      <p className="m-0 text-xs text-od-muted">{AUDIT_SOURCE_NOTE}</p>
      {events.length === 0 ? (
        <p className="m-0 text-sm text-od-muted">
          No changes yet. An edit through OnlyDash shows here with the person,
          the time and each changed value.
        </p>
      ) : (
        <AuditTimeline events={events} showRowKey={false} showTab={false} />
      )}
    </div>
  );
};

export default RowHistory;
